/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient, HttpParams} from '@angular/common/http';
import {Component, EventEmitter, Input, OnInit, Output, signal, ViewChild} from '@angular/core';
import {AutoComplete} from 'primeng/autocomplete';
import {EsciMessageService} from '../../shared/esci-message.service';
import {environment} from '../../../environments/environment';
import {AuthenticationService} from '../../core/authentication/authentication.service';
import {SearchUser, User} from '../../shared/model/user';
import {CacheService} from '../../service/cache-service.service';

@Component({
    selector: 'app-auto-search',
    templateUrl: './auto-search.component.html',
    styleUrls: ['./auto-search.component.scss'],
    standalone: false
})

export class AutoSearchComponent implements OnInit {
    static MAX_USERS_TO_RETURN = 15;
    static TOO_MANY_RESULTS = {fullName: '...Enter more characters to see more results...'};
    static NO_RESULTS = {fullName: 'No matching results!'};
    // These are the bound parameters:
    @Input() label: string;                  // The label that will appear to the left of the input box
    @Input() ngRequired = false;     // The variable that requires the field to be filled
    @Input() showMeButton = true;
    @Input() readonly = false;
    @Input() initialUser: User; // The user object that we initialize with
    ngSelectedUser: SearchUser; // This is the variable that holds the displayed full name
    @Output() userSelected = new EventEmitter<any>();
    @ViewChild('userAutoSearch') autoComplete?: AutoComplete;

    // These are computed values:
    lastSearchTerm = null;
    private readonly filteredNamesSignal = signal<SearchUser[]>([]);
    private latestSearchRequestId = 0;

    // Dependencies:
    public user: SearchUser;

    constructor(private $http: HttpClient, private messageService: EsciMessageService,
                private authService: AuthenticationService, private cacheService: CacheService) {
        this.user = new SearchUser();
        this.user.setUser(this.authService.userInfo);
        this.ngSelectedUser = new SearchUser();
    }

    get filteredNames(): SearchUser[] {
        return this.filteredNamesSignal();
    }

    set filteredNames(value: SearchUser[]) {
        this.filteredNamesSignal.set(value || []);
    }

    autoSelectUserSearchResults(results) {
        console.log('in autoSelectUserSearchResults ' + results.fullName);
        if (results === AutoSearchComponent.TOO_MANY_RESULTS
            || results === AutoSearchComponent.NO_RESULTS) {
            // revert the search box's value to the last searched term:
            results.fullName = this.lastSearchTerm;
            results.gmin = '';
        }
        this.ngSelectedUser = results;
        if (this.userSelected && this.ngSelectedUser.gmin) {
            this.userSelected.emit(this.ngSelectedUser);
        }
    }

    autoSearchUserSimplified(term) {
        let searchCriteria = term.query;
        this.lastSearchTerm = searchCriteria;
        const requestId = ++this.latestSearchRequestId;
        // Clear stale suggestions while a new query is loading.
        this.filteredNames = [];
        queueMicrotask(() => this.hideSuggestionsPanel());
        searchCriteria = encodeURIComponent(searchCriteria);
        const params = new HttpParams().set('allFields', searchCriteria)
        .set('maxUsersToReturn', AutoSearchComponent.MAX_USERS_TO_RETURN.toString());
        this.cacheService.getCacheable( environment.apiUrl + '/esciUI/userSearch/', {params: params}).subscribe({
            next: (event: any) => {
                if (requestId !== this.latestSearchRequestId) {
                    return;
                }
                let autoUserSearchResults = null;
                if (event.body) {
                    // If any entry has the same first & last name as another entry,
                    // put the email address in parentheses at the end of the name
                    // First, we make a Map that contains all the names, but as we add
                    // entries to it, if we find the name already there, we put the name in a dupNameMap.
                    const namesMap = new Map();
                    const dupNamesMap = new Map();
                    autoUserSearchResults = [];
                    for (const entry of event.body) {
                        if (entry.gmin) {
                            const user = new SearchUser();
                            user.setUser(new User(entry.givenName, entry.sn, entry.gmin, entry.uid, entry.email));
                            autoUserSearchResults.push(user);
                            if (namesMap.has(user.fullName)) {
                                dupNamesMap.set(user.fullName, '');
                            }
                            namesMap.set(user.fullName, '');
                        }
                    }
                    // Now we can iterate over the results, and if the name is in the dupNameMap, add the email to displayName
                    for (const entry of autoUserSearchResults) {
                        if (dupNamesMap.has(entry.fullName)) {
                            entry.fullName += ' (' + entry.email + ')';
                        }
                    }
                    // Sort the results, so it's easier to read large result sets:
                    autoUserSearchResults.sort((a, b) => a.fullName.toUpperCase().localeCompare(b.fullName.toUpperCase()));
                    if (autoUserSearchResults.length >= AutoSearchComponent.MAX_USERS_TO_RETURN) {
                        autoUserSearchResults.length = AutoSearchComponent.MAX_USERS_TO_RETURN;
                        // replace the last entry with a note that there are more entries to search
                        autoUserSearchResults[AutoSearchComponent.MAX_USERS_TO_RETURN - 1] = AutoSearchComponent.TOO_MANY_RESULTS;
                    }
                }
                if (autoUserSearchResults?.length === 0) {
                    autoUserSearchResults = [AutoSearchComponent.NO_RESULTS];
                }
                if (requestId === this.latestSearchRequestId) {
                    this.filteredNames = autoUserSearchResults ?? [];
                    if (this.filteredNames.length > 0) {
                        this.showSuggestionsPanel();
                    } else {
                        this.hideSuggestionsPanel();
                    }
                }
            },
            error: (err: any) => {
                if (requestId !== this.latestSearchRequestId) {
                    return;
                }
                console.log(err);
                this.messageService.processError('Could not do user lookup. Please try again later.');
            }
        });
    }

    private showSuggestionsPanel(): void {
        const autoCompleteAny = this.autoComplete as any;
        if (typeof autoCompleteAny?.show === 'function') {
            autoCompleteAny.show();
        }
    }

    private hideSuggestionsPanel(): void {
        const autoCompleteAny = this.autoComplete as any;
        if (typeof autoCompleteAny?.hide === 'function') {
            autoCompleteAny.hide();
        }
    }

    ngOnInit(): void {
        if (this.initialUser && (this.initialUser instanceof User || this.initialUser['gmin'])) {
            this.ngSelectedUser.setUser(this.initialUser);
        } else if (this.initialUser && this.initialUser instanceof SearchUser) {
            this.ngSelectedUser = this.initialUser;
        } else if (this.initialUser && typeof this.initialUser === 'string') {
            this.autoSearchUserSimplified({query: this.initialUser});
        }
    }

    toggleImageFallback(event: Event, showFallback: boolean): void {
        const imageElement = event.target as HTMLElement | null;
        const imageContainer = imageElement?.parentElement;
        const fallbackElement = imageContainer?.querySelector('.profile-image-fallback-icon') as HTMLElement | null;

        if (!imageElement || !fallbackElement) {
            return;
        }

        imageElement.style.display = showFallback ? 'none' : '';
        fallbackElement.style.display = showFallback ? 'inline-block' : 'none';
    }
}
